<script setup>
import { ref, onMounted, onBeforeMount, onBeforeUnmount, computed } from 'vue';
import { useStore } from 'vuex';
import api from '@/api';
import Swal from 'sweetalert2';
import setNavPills from "@/assets/js/nav-pills.js";
import setTooltip from "@/assets/js/tooltip.js";

const user = ref(null);
const loading = ref(false);
const cargandoPerfil = ref(true);
const errors = ref({});

const form = ref({
    nombre: '',
    identificacion: '',
    email: '',
    telefono: '',
    direccion: '',
    fecha_nacimiento: '',
    genero: ''
});

const rolNombre = ref('');
const avatarUrl = ref('');
const avatarFile = ref(null);
const preview = ref(null);

const GENEROS = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
    { value: 'Otro', label: 'Otro' }
];

const iniciales = computed(() => {
    const n = form.value.nombre?.trim() || '';
    if (!n) return '—';
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() || '').join('') || '—';
});

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

function validateImageFile(file) {
    if (!ALLOWED_MIME.includes(file.type)) {
        Swal.fire('Archivo no válido', 'Formato permitido: JPG, PNG o WEBP.', 'warning');
        return false;
    }
    if (file.size > MAX_FILE_SIZE) {
        Swal.fire('Archivo muy grande', 'El tamaño máximo es 2 MB.', 'warning');
        return false;
    }
    return true;
}

function revokePreview() {
    if (preview.value && preview.value.startsWith('blob:')) {
        URL.revokeObjectURL(preview.value)
    }
}

function onFile(e) {
    const f = e.target.files?.[0];
    if (f && !validateImageFile(f)) {
        e.target.value = '';
        avatarFile.value = null;
        revokePreview()
        preview.value = null;
        return;
    }
    revokePreview()
    avatarFile.value = f || null;
    preview.value = f ? URL.createObjectURL(f) : null;
}

async function save() {
    errors.value = {};
    if (!form.value.nombre?.trim()) errors.value.nombre = 'El nombre es obligatorio';
    if (!form.value.identificacion?.trim()) errors.value.identificacion = 'La identificación es obligatoria';
    if (form.value.email && form.value.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email.trim())) {
        errors.value.email = 'Email no válido'
    }
    if (Object.keys(errors.value).length > 0) return;

    try {
        loading.value = true;
        const fd = new FormData();
        Object.entries(form.value).forEach(([key, val]) => {
            if (val !== '' && val !== null && val !== undefined) {
                fd.append(key, val);
            }
        });
        if (avatarFile.value) {
            fd.append('avatar', avatarFile.value);
        }

        // PUT perfil propio (requiere perfil.propio, no expone rol_id/estado)
        const { data } = await api.put(`/users/perfil`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Backend PUT /users/perfil devuelve usuario con avatar_url y rol
        const prevRaw = localStorage.getItem("user");
        let prev = {};
        try { prev = prevRaw ? JSON.parse(prevRaw) : {}; } catch (_err) { prev = {}; }
        const updatedUser = {
            ...prev,
            ...data,
            // preservar permisos/rol si backend no los devuelve
            permisos: data.permisos || prev.permisos || [],
            rol: data.rol || data.rol_nombre || prev.rol,
            rol_id: data.rol_id || prev.rol_id,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        Swal.fire({
            icon: 'success',
            title: 'Perfil actualizado',
            text: 'Tus datos personales han sido actualizados correctamente.',
            confirmButtonText: 'Aceptar'
        }).then(() => {
            window.location.reload(); // Recarga la aplicación para refrescar el menú y navegación
        });
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error(err);
        const msg = err.response?.data?.message || err.message || 'Error al actualizar el perfil';
        Swal.fire('Error', msg, 'error');
    } finally {
        loading.value = false;
    }
}

const body = document.getElementsByTagName("body")[0];
const store = useStore();

onBeforeMount(() => {
  store.state.imageLayout = "profile-overview";
  store.state.showNavbar = false;
  store.state.showFooter = true;
  store.state.hideConfigButton = true;
  body.classList.add("profile-overview");
});

onBeforeUnmount(() => {
  revokePreview()
  store.state.isAbsolute = false;
  store.state.imageLayout = "default";
  store.state.showNavbar = true;
  store.state.showFooter = true;
  store.state.hideConfigButton = false;
  body.classList.remove("profile-overview");
});

async function cargarPerfil() {
    try {
        cargandoPerfil.value = true;
        // Intenta perfil propio (requiere perfil.propio) sin exponer ID; fallback a /users/:id
        let data = null;
        try {
            const resp = await api.get(`/users/perfil`);
            // GET /users/perfil devuelve {mensaje, usuario, rol, permisos}
            data = resp.data.usuario || resp.data;
            // Enriquecer con rol/permisos del wrapper si existen
            if (resp.data.rol) data.rol = resp.data.rol;
            if (resp.data.permisos) data.permisos = resp.data.permisos;
            // Si no trae avatar_url, intentar obtener detalle completo vía /users/:id solo si es ADMIN (tiene socios.ver)
            // Para EMPLEADO, /users/:id propio sería bloqueado si es EMPLEADO, así que evitamos
            if (!data.avatar_url && data.id) {
                try {
                    const detail = await api.get(`/users/${data.id}`);
                    data = { ...data, ...detail.data };
                } catch (_err2) { /* ignora, perfil propio ya es suficiente */ void _err2; }
            }
        } catch (_e) {
            // Fallback legacy: id desde localStorage
            let id = null;
            try { const raw = localStorage.getItem("user"); if (raw) id = JSON.parse(raw).id; } catch (_err3) { void _err3; }
            if (!id) throw _e;
            const resp2 = await api.get(`/users/${id}`);
            data = resp2.data;
        }
        user.value = data;

        const rawFecha = data.fecha_nacimiento || '';
        const fechaNorm = rawFecha ? rawFecha.split('T')[0].slice(0,10) : '';

        form.value.nombre = data.nombre || '';
        form.value.identificacion = data.identificacion || '';
        form.value.email = data.email || '';
        form.value.telefono = data.telefono || '';
        form.value.direccion = data.direccion || '';
        form.value.fecha_nacimiento = fechaNorm;
        form.value.genero = data.genero || '';
        rolNombre.value = data.rol?.nombre || data.rol || data.rol_nombre || '—';
        avatarUrl.value = data.avatar_url || data.avatar ? (data.avatar_url || `/static/${data.avatar}`) : '';

        try {
            const rawPrev = localStorage.getItem("user");
            const prev = rawPrev ? JSON.parse(rawPrev) : {};
            localStorage.setItem("user", JSON.stringify({ ...prev, ...data }));
        } catch (_err) { /* ignore parse */ }
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error("Error cargando perfil:", e);
        const rawUser = localStorage.getItem("user");
        if (rawUser) {
            try {
                const fallback = JSON.parse(rawUser);
                user.value = fallback;
                form.value.nombre = fallback.nombre || '';
                form.value.identificacion = fallback.identificacion || '';
                form.value.email = fallback.email || '';
                form.value.telefono = fallback.telefono || '';
                form.value.direccion = fallback.direccion || '';
                form.value.fecha_nacimiento = (fallback.fecha_nacimiento || '').split('T')[0].slice(0,10);
                form.value.genero = fallback.genero || '';
                rolNombre.value = fallback.rol?.nombre || fallback.rol || '—';
                avatarUrl.value = fallback.avatar_url || '';
            } catch (_err) { /* ignore parse */ }
        }
        Swal.fire('Aviso', e.response?.data?.message || 'No se pudo cargar el perfil actualizado, mostrando datos locales', 'warning');
    } finally {
        cargandoPerfil.value = false;
    }
}

onMounted(() => {
    store.state.isAbsolute = true;
    setNavPills();
    setTooltip();
    cargarPerfil();
});
</script>

<template>
  <div class="container-fluid py-4">
    <div class="page-header min-height-300 border-radius-xl d-flex align-items-center justify-content-center"
      :style="{ backgroundImage: `url(${require('@/assets/img/bg-profile.jpg')})`, backgroundPositionY: '50%' }">
      <span class="mask bg-gradient-success opacity-6"></span>
      <h3 class="text-white z-index-1 mb-0">Mi Perfil</h3>
    </div>
    
    <div class="card card-body mx-4 mt-n6 shadow-lg">
      <div class="row gx-4 align-items-center">
        <div class="col-auto">
          <div class="avatar-wrapper position-relative" style="width: 80px; height: 80px; border-radius: 50%; overflow: hidden; border: 3px solid #fff; box-shadow: 0 0 10px rgba(0,0,0,0.15);">
            <img v-if="preview" :src="preview" class="w-100 h-100 object-fit-cover" alt="Vista previa" />
            <img v-else-if="avatarUrl" :src="avatarUrl" class="w-100 h-100 object-fit-cover" alt="Avatar" />
            <div v-else class="w-100 h-100 bg-gradient-secondary d-flex align-items-center justify-content-center text-white fw-bold text-xl">{{ iniciales }}</div>
          </div>
        </div>
        <div class="col my-auto">
          <div class="h-100">
            <h5 class="mb-1">{{ form.nombre || 'Nombre de Usuario' }}</h5>
            <p class="mb-0 font-weight-bold text-sm text-secondary">{{ rolNombre }}</p>
          </div>
        </div>
      </div>
      
      <hr class="horizontal dark my-4" />
      <div v-if="cargandoPerfil" class="text-center py-4 text-muted">
        <i class="fa fa-spinner fa-spin me-2"></i> Cargando perfil...
      </div>
      <form v-else @submit.prevent="save">
        <div class="row g-3">
          <!-- Nombre -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Nombre Completo *</label>
            <input v-model="form.nombre" type="text" class="form-control" :class="{ 'is-invalid': errors.nombre }" />
            <div class="invalid-feedback" v-if="errors.nombre">{{ errors.nombre }}</div>
          </div>
          
          <!-- Identificación -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Identificación / Cédula *</label>
            <input v-model="form.identificacion" type="text" class="form-control" :class="{ 'is-invalid': errors.identificacion }" />
            <div class="invalid-feedback" v-if="errors.identificacion">{{ errors.identificacion }}</div>
          </div>
          
          <!-- Email -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Correo Electrónico</label>
            <input v-model="form.email" type="email" class="form-control" :class="{ 'is-invalid': errors.email }" />
            <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
          </div>
          
          <!-- Teléfono -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Teléfono</label>
            <input v-model="form.telefono" type="text" class="form-control" />
          </div>
          
          <!-- Dirección -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Dirección</label>
            <input v-model="form.direccion" type="text" class="form-control" />
          </div>
          
          <!-- Fecha Nacimiento -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Fecha de Nacimiento</label>
            <input v-model="form.fecha_nacimiento" type="date" class="form-control" />
          </div>
          
          <!-- Género -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Género</label>
            <select v-model="form.genero" class="form-select">
              <option value="">— Seleccione —</option>
              <option v-for="g in GENEROS" :key="g.value" :value="g.value">{{ g.label }}</option>
            </select>
          </div>
          
          <!-- Rol (Lectura únicamente) -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Rol asignado</label>
            <input :value="rolNombre" type="text" class="form-control bg-light" readonly />
            <small class="text-muted">El rol de usuario no es editable por motivos de seguridad.</small>
          </div>

          <!-- Foto de Perfil -->
          <div class="col-12 col-md-6">
            <label class="form-label fw-bold">Foto de Perfil</label>
            <input type="file" @change="onFile" accept="image/jpeg,image/png,image/webp" class="form-control" />
            <small class="text-muted">Formatos permitidos: JPG, PNG, WEBP. Máx. 2 MB.</small>
          </div>
        </div>
        
        <div class="text-end mt-4">
          <button type="submit" class="btn btn-success" :disabled="loading">
            <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i> Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.object-fit-cover {
  object-fit: cover;
}
</style>
 